import { isNotNullOrWhitespace } from "../../../framework/utils";
import { MarkdownComponent, strictUrlPattern } from "./core";
import { desanitizeMarkdown, sanitizeSymbols } from "./sanitization";

// DISABLED: This can cause an infinite Regex loop, and isn't worth the trouble now (maybe ever)
const applyDoubleCodeDecoration = (line: string) => {
  // const pattern = /``((?:(?:`(?!`))?[^`]*)*)``/g;
  let value = line;
  /* 
  let result = pattern.exec(value);
  while(!!result) {
    const sanitizedContent = sanitizeSymbols(result[1]);
    value = value.replaceAll(result[0], `<code>${sanitizedContent}</code>`);
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }//*/

  return value;
}
const applyCodeDecoration = (line: string) => {
  const pattern = /`([^`]+)`/g;
  let value = line;
  let result = pattern.exec(value);
  while(!!result) {
    const sanitizedContent = sanitizeSymbols(result[1]);
    value = value.replaceAll(result[0], `<code>${sanitizedContent}</code>`);
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }

  return value;
}
const sanitizeCodeContent = (line: string) => {
  let value = applyDoubleCodeDecoration(line);
  value = applyCodeDecoration(value)
    .replace(/<\/?code>/g, '`'); // we normalize code such that all double code decorators (``) become single

  return value;
}

const applyLinkAndImageDecoration = (componentType: MarkdownComponent, line: string) => {
  const pattern = /!?\[([^[\]]*)\]\s*\(\s*([a-z0-9-._~!$&'(*+,;=/?:#%@]+)((?:\s+"[^"]+")|(?:\s+'[^']+'))?\s*\)/gi;
  let value = line;
  let result = pattern.exec(value);
  while(!!result) {
    const url = result[2];
    if(strictUrlPattern.test(url)) { // TODO: decode escaped paren/square bracket chars before testing
      const title = result.length === 4 && isNotNullOrWhitespace(result[3]) ? ` title=${result[3].trim()}` : '';
      // prevent foolery like underscores in a URL being interpreted as italic text
      const safeUrl = sanitizeSymbols(url);
      if(result[0][0] === '!' && componentType !== MarkdownComponent.Heading && componentType !== MarkdownComponent.TimingSpec) {
        const alt = isNotNullOrWhitespace(result[1]) ?
          ` alt="${sanitizeSymbols(result[1].trim().replaceAll('"', '&quot;'))}"` : '';
        value = value.replaceAll(result[0], `<img src="${safeUrl}"${title}${alt}/>`);
      } else if(componentType !== MarkdownComponent.TimingSpec) {
        const text = isNotNullOrWhitespace(result[1]) ? result[1].trim() : safeUrl;
        value = value.replaceAll(result[0], `<a href="${safeUrl}"${title}>${text}</a>`);
      } else {
        const text = isNotNullOrWhitespace(result[1]) ? result[1].trim() : safeUrl;
        value = value.replaceAll(result[0], text);
      }
    } else {
      const deactivatedLink = result[0].replaceAll('(', '@lpr;')
        .replaceAll(')', '@rpr;')
        .replaceAll('!', '@exc;')
        .replaceAll('[', '@lsq;')
        .replaceAll(']', '@rsq;');
      value = value.replaceAll(result[0], deactivatedLink);
    }
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }

  return value;
}
const applyQuickLinkAndImageDecoration = (componentType: MarkdownComponent, line: string) => {
  const pattern = /!?&lt;\s*([a-z]+:\/\/[a-z0-9-._~!$&'()*+,;=/?:#%@]+)\s*>/gi;
  let value = line;
  let result = pattern.exec(value);
  while(!!result) {
    const url = result[1];
    if(strictUrlPattern.test(url)) {
      // prevent foolery like underscores in a URL being interpreted as italic text
      const safeUrl = sanitizeSymbols(url);
      if(result[0][0] === '!' && componentType !== MarkdownComponent.Heading && componentType !== MarkdownComponent.TimingSpec) {
        value = value.replaceAll(result[0], `<img src="${safeUrl}"/>`);
      } else if(componentType !== MarkdownComponent.TimingSpec) {
        value = value.replaceAll(result[0], `<a href="${safeUrl}">${safeUrl}</a>`);
      } else {
        value = value.replaceAll(result[0], safeUrl);
      }
    } else {
      const deactivatedLink = result[0].replaceAll('>', '@gt;');
      value = value.replaceAll(result[0], deactivatedLink);
    }
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }

  return value;
}

const applyStandardDecoration = (componentType: MarkdownComponent, line: string, expression: string, standardTemplate: string, headerTimingTemplate: string) => {
  let value = line.replaceAll(expression, '%');
  const pattern = /%([^%]+)%/g;
  let result = pattern.exec(value);
  while(!!result) {
    const content = result[1];
    if(componentType !== MarkdownComponent.Heading && componentType !== MarkdownComponent.TimingSpec) {
      const replacement = standardTemplate.replace('$', content);
      value = value.replaceAll(result[0], replacement);
    } else {
      const replacement = headerTimingTemplate.replace('$', content);
      value = value.replaceAll(result[0], replacement);
    }
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }

  value = value.replaceAll('%', expression);
  return value;
}
const applyBoldItalicDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<strong><em>$</em></strong>';
  const headerTimingTemplate = '<em>$</em>';

  let value = applyStandardDecoration(componentType, line, '***', standardTemplate, headerTimingTemplate);
  value = applyStandardDecoration(componentType, value, '___', standardTemplate, headerTimingTemplate);

  return value;
}
const applyBoldDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<strong>$</strong>';
  const headerTimingTemplate = '$';

  let value = applyStandardDecoration(componentType, line, '**', standardTemplate, headerTimingTemplate);
  value = applyStandardDecoration(componentType, value, '__', standardTemplate, headerTimingTemplate);

  return value;
}
const applyItalicDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<em>$</em>';
  const headerTimingTemplate = '<em>$</em>';

  let value = applyStandardDecoration(componentType, line, '*', standardTemplate, headerTimingTemplate);
  value = applyStandardDecoration(componentType, value, '_', standardTemplate, headerTimingTemplate);

  return value;
}
const applyUnderlineDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<b>$</b>';

  return applyStandardDecoration(componentType, line, '===', standardTemplate, standardTemplate);
}
const applyHighlightDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<mark>$</mark>';

  return applyStandardDecoration(componentType, line, '==', standardTemplate, standardTemplate);
}
const applyStrikethroughDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<del>$</del>';

  return applyStandardDecoration(componentType, line, '~~', standardTemplate, standardTemplate);
}
const applySubscriptDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<sub>$</sub>';

  return applyStandardDecoration(componentType, line, '~', standardTemplate, standardTemplate);
}
const applySuperscriptDecoration = (componentType: MarkdownComponent, line: string) => {
  const standardTemplate = '<sup>$</sup>';

  return applyStandardDecoration(componentType, line, '^', standardTemplate, standardTemplate);
}

const applyLongDashDecoration = (line: string) => {
  const pattern = /(?:^---[^-])|(?:[^-]---[^-])|(?:[^-]---$)/g;
  let value = line;
  let result = pattern.exec(value);
  while(!!result) {
    value = value.replace(result[0], result[0].replace('---', '&mdash;'));
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }

  return value;
}

const applyMidDashDecoration = (line: string) => {
  const pattern = /(?:^--[^-])|(?:[^-]--[^-])|(?:[^-]--$)/g;
  let value = line;
  let result = pattern.exec(value);
  while(!!result) {
    value = value.replace(result[0], result[0].replace('--', '&ndash;'));
    pattern.lastIndex = 0;
    result = pattern.exec(value);
  }

  return value;
}

const applyDashDecoration = (line: string) => {
  let value = applyLongDashDecoration(line);
  value = applyMidDashDecoration(value);

  return value;
}

export const applyTextDecoration = (componentType: MarkdownComponent, line: string) => {
  switch(componentType) {
    case MarkdownComponent.BlockquoteFragment:
      componentType = MarkdownComponent.Blockquote;
      break;
    case MarkdownComponent.OrderedListFragment:
      componentType = MarkdownComponent.OrderedList;
      break;
    case MarkdownComponent.TableFragment:
      componentType = MarkdownComponent.Table;
      break;
    case MarkdownComponent.UnorderedListFragment:
      componentType = MarkdownComponent.UnorderedList;
      break;
  }

  let value = sanitizeCodeContent(line);
  value = applyLinkAndImageDecoration(componentType, value);
  value = applyQuickLinkAndImageDecoration(componentType, value);
  value = applyCodeDecoration(value);
  value = applyBoldItalicDecoration(componentType, value);
  value = applyBoldDecoration(componentType, value);
  value = applyItalicDecoration(componentType, value);
  value = applyUnderlineDecoration(componentType, value);
  value = applyHighlightDecoration(componentType, value);
  value = applyStrikethroughDecoration(componentType, value);
  value = applySubscriptDecoration(componentType, value);
  value = applySuperscriptDecoration(componentType, value);
  value = applyDashDecoration(value);

  value = desanitizeMarkdown(value);
  return value;
}